import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts';

/**
 * @component QuickStatsBarChart
 * @description Horizontal bar chart showing quick stats (totalEmails, delivered, opens, linkClicks)
 * @param {Object} props
 * @param {Object} props.stats - Stats object with totalEmails, delivered, opens, linkClicks
 * @param {boolean} [props.loading=false] - Loading state
 * @param {number} [props.height=200] - Chart height
 * @returns {JSX.Element}
 */
export function QuickStatsBarChart({ 
  stats = {},
  loading = false,
  height = 200
}) {
  const theme = useTheme();

  // Prepare data for horizontal bar chart
  const chartData = [
    {
      label: 'Total Emails',
      value: stats.totalEmails || 0,
    },
    {
      label: 'Delivered',
      value: stats.delivered || 0,
    },
    {
      label: 'Opens',
      value: stats.opens || 0,
    },
    {
      label: 'Link Clicks',
      value: stats.linkClicks || 0,
    },
  ];

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minHeight: height }}>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Paper>
    );
  }

  if (!stats || Object.keys(stats).length === 0 || chartData.every(item => item.value === 0)) {
    return (
      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', minHeight: height }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <BarChart
        layout="horizontal"
        margin={{ left: 80 }}
        yAxis={[
          {
            data: chartData.map(item => item.label),
            scaleType: 'band',
          },
        ]}
        series={[
          {
            data: chartData.map(item => item.value),
            color: theme.palette.primary.main,
          },
        ]}
        height={height}
        sx={{
          '& .MuiChartsAxis-root': {
            fontSize: '0.75rem',
          },
          '& .MuiChartsBarElement-root rect': {
            rx: '4px',
            ry: '4px',
          },
        }}
      />
    </Box>
  );
}

